import React from 'react';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

/**
 * Rodapé PADRÃO de paginação das listagens.
 *
 * Fica no FIM da lista, não dentro do painel de filtros. Já esteve nos dois lugares:
 * juntar tudo no painel parecia organizar, mas na prática obrigava a rolar de volta ao
 * topo só para avançar de página — o oposto do que se quer depois de percorrer a lista
 * inteira. Aqui a navegação está onde o olho chega.
 *
 * Existe como componente compartilhado para as páginas não voltarem a divergir: antes
 * cada uma tinha o seu rodapé (uma com "Anterior/Próxima", outras com MUI Pagination,
 * cada uma com um espaçamento).
 *
 * Uso:
 *   <PaginacaoFooter
 *       page={page} totalPages={totalPages} onPageChange={setPage}
 *       itemsPerPage={size} onItemsPerPageChange={setSize}
 *       itemsPerPageOptions={[10, 20, 50]} loading={loading}
 *   />
 *
 * @param {number}   page                  página ATUAL, 1-based (converta na borda se
 *                                         o seu estado for 0-based, como o do Spring).
 * @param {number}   totalPages            0 esconde o rodapé inteiro.
 * @param {Function} onPageChange          recebe a página 1-based.
 * @param {number}   [itemsPerPage]        omita para esconder o seletor de tamanho.
 * @param {Function} [onItemsPerPageChange] recebe o número já convertido.
 */
export default function PaginacaoFooter({
    page = 1,
    totalPages = 0,
    onPageChange,
    itemsPerPage,
    onItemsPerPageChange,
    itemsPerPageOptions = [5, 10, 20, 50],
    loading = false,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const mostraSeletor =
        itemsPerPage != null && typeof onItemsPerPageChange === 'function';

    // Uma página só e sem seletor = nada a navegar; o rodapé seria puro ruído.
    if (totalPages <= 1 && !mostraSeletor) return null;
    if (typeof onPageChange !== 'function') return null;

    return (
        <Paper
            elevation={0}
            variant="outlined"
            sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: mostraSeletor ? 'space-between' : 'center',
                gap: 2,
            }}
        >
            {mostraSeletor && (
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    <FormControl
                        size="small"
                        sx={{ minWidth: 108, width: { xs: '100%', sm: 'auto' } }}
                    >
                        <InputLabel>Itens por pág.</InputLabel>
                        <Select
                            label="Itens por pág."
                            value={itemsPerPage}
                            onChange={(e) =>
                                onItemsPerPageChange(Number(e.target.value))
                            }
                            disabled={loading}
                            sx={{ borderRadius: 2 }}
                        >
                            {itemsPerPageOptions.map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* No mobile o seletor sozinho numa linha fica órfão; o total
                        dá contexto e ocupa o espaço que sobra. */}
                    {totalPages > 0 && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {totalPages} {totalPages === 1 ? 'página' : 'páginas'}
                        </Typography>
                    )}
                </Stack>
            )}

            {totalPages > 1 && (
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => onPageChange(v)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    disabled={loading}
                    size={isMobile ? 'small' : 'medium'}
                    // No mobile, com 100+ páginas os botões estouram a largura.
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                />
            )}
        </Paper>
    );
}
